import { useCallback, useEffect, useRef, useState } from "react";
import {
  createAddressAutocompleteSessionToken,
  fetchAddressAutocomplete,
  resolveAddressPlaceDetails,
  type AddressAutocompletePick,
  type AddressAutocompleteSuggestion,
} from "@/lib/addressAutocompleteClient";

const DEBOUNCE_MS = 280;

export function useAddressAutocomplete() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressAutocompleteSuggestion[]>([]);
  const [provider, setProvider] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef(createAddressAutocompleteSessionToken());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  const resetSession = useCallback(() => {
    sessionTokenRef.current = createAddressAutocompleteSessionToken();
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setBusy(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const seq = ++requestSeq.current;
      setBusy(true);
      setError(null);
      void fetchAddressAutocomplete({
        query: trimmed,
        sessionToken: sessionTokenRef.current,
      })
        .then((res) => {
          if (seq !== requestSeq.current) return;
          setProvider(res.provider);
          setSuggestions(res.suggestions);
          setOpen(res.suggestions.length > 0);
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return;
          setError(e instanceof Error ? e.message : String(e));
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => {
          if (seq === requestSeq.current) setBusy(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const pick = useCallback(
    async (suggestion: AddressAutocompleteSuggestion): Promise<AddressAutocompletePick> => {
      setBusy(true);
      setError(null);
      try {
        const resolved = await resolveAddressPlaceDetails({
          placeId: suggestion.placeId,
          sessionToken: sessionTokenRef.current,
        });
        setQuery(resolved.formatted || suggestion.label);
        setSuggestions([]);
        setOpen(false);
        resetSession();
        return resolved;
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [resetSession],
  );

  return {
    query,
    setQuery,
    suggestions,
    provider,
    busy,
    error,
    open,
    setOpen,
    pick,
    clearSuggestions,
    resetSession,
  };
}
